<script setup lang="ts">
import { computed } from 'vue';

import { useAccount } from '@/composables/useAccount';
import { mapDetailsItem, useTransactionDetails } from '@/composables/useTransactionDetails';
import { filterDefined } from '@/helpers/arrays';
import { useI18n } from '@/references/i18n';

import UiImportant from '@/components/ui/UiImportant.vue';
import type { UiListPairGroup, UiListPairItem } from '@/components/ui/UiListPair.types';
import UiListPair from '@/components/ui/UiListPair.vue';
import UiModal from '@/components/ui/UiModal.vue';
import UiTitle from '@/components/ui/UiTitle.vue';

const { t } = useI18n();
const { isAuthed } = useAccount();
const { feeModalOpened, closeFeeModal, feeValues } = useTransactionDetails();

const title = computed((): string => {
  if (feeValues.value === undefined) {
    return '';
  }

  return t(`activity.details.${feeValues.value.value.titleKey}`);
});

const details = computed((): UiListPairGroup[] => {
  if (feeValues.value === undefined) {
    return [];
  }

  return [
    {
      items: filterDefined(
        feeValues.value.value.items.map((item): UiListPairItem | undefined => {
          const mapped = mapDetailsItem(item);

          if (mapped === undefined) {
            return undefined;
          }

          return {
            label: mapped.name,
            value: mapped.value,
            image: mapped.iconUrl
          };
        })
      )
    }
  ];
});
</script>

<template>
  <UiModal
    class="transaction-details-fee-modal"
    :model-value="isAuthed && feeModalOpened"
    @update:model-value="closeFeeModal"
  >
    <UiImportant class="transaction-details-fee-modal__icon" mod="question" />
    <UiTitle class="transaction-details-fee-modal__title" :level="2" size="xl" align="center">
      {{ title }}
    </UiTitle>
    <UiListPair :details="details" />
  </UiModal>
</template>

<style scoped>
.transaction-details-fee-modal__icon {
  margin: 0 auto 24px;
}

.transaction-details-fee-modal__title {
  margin: 0 0 40px;
}
</style>
